/**
 * @module  public/js/core/firebase-service.js
 * @summary Boots a singleton Firebase App (Auth + Firestore + optional GA).
 *
 * @description
 * • Reads the resolved environment from the default `endpoints` instance
 *   (`production` | `eamSandler` | `acuteIncite` | `dev`).
 * • Maps that env to its Firebase config.
 * • Creates (or re-uses) the Firebase App and exports:
 *       firebaseApp · auth · db · functions · analytics · env · isProduction
 *
 * ## CDN dependencies  (v9.17.2 modular build; keep versions aligned)
 * – firebase-app, firebase-auth, firebase-firestore, firebase-functions,
 *   firebase-analytics
 *
 * @author   TopSail Dev Center
 * @version  2.1.0
 * @since    2025-05-19
 */

/* ----------  Firebase imports (one version everywhere)  ---------- */
import {
  initializeApp,
  getApps,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-app.js';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  getToken as getAppCheckSdkToken,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-app-check.js';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-auth.js';
import {
  getFirestore,
  initializeFirestore,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { getFunctions } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-functions.js';
import { getAnalytics } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-analytics.js';
import {
  FORCE_LONG_POLLING_QUERY,
  tsForceLongPolling,
} from './firebase-long-polling-toggle.js';

/* ----------  1️⃣  Environment from EndpointService  -------------- */
import endpoints from './endpoints-service.js';
export const env = endpoints.env; // 'production' | 'eamSandler' | 'acuteIncite' | 'dev'
export const isProduction = env === 'production';

/* ----------  2️⃣  Config map (fill keys once; no secrets) --------- */
const firebaseConfigs = {
  production: {
    apiKey: 'AIzaSyDAGUTVXwcmEALifzwteYZbCYK2yT2ESnQ',
    authDomain: 'topsailrevenue.firebaseapp.com',
    projectId: 'topsailrevenue',
    storageBucket: 'topsailrevenue.firebasestorage.app',
    messagingSenderId: '799731024033',
    appId: '1:799731024033:web:93ea7e9a5cb6639ddd70e0',
    measurementId: 'G-Z1MZ33YVHK',
  },
  eamSandler: {
    apiKey: 'AIzaSyADggEEAYDqwY_OjiycjYf5MB22Sp8qRWc',
    authDomain: 'topsail-eamsandler.firebaseapp.com',
    projectId: 'topsail-eamsandler',
    storageBucket: 'topsail-eamsandler.firebasestorage.app',
    messagingSenderId: '736109494854',
    appId: '1:736109494854:web:6d6a537e49bd2ca0fdc047',
    measurementId: 'G-RX7HELD03K',
  },
  acuteIncite: {
    apiKey: 'AIzaSyDts1mVAGfwWRXfLl0sTFn_-7aCT3h3XIw',
    authDomain: 'topsail-acuteincite.firebaseapp.com',
    projectId: 'topsail-acuteincite',
    storageBucket: 'topsail-acuteincite.firebasestorage.app',
    messagingSenderId: '640484928743',
    appId: '1:640484928743:web:2c330483fa0b0baddd8642',
  },
  dev: {
    apiKey: 'AIzaSyDZqPMMERO8C14M6Trj-YawdMlcI33vcjU',
    authDomain: 'leadepic-for-linkedin.firebaseapp.com',
    projectId: 'leadepic-for-linkedin',
    storageBucket: 'leadepic-for-linkedin.firebasestorage.app',
    messagingSenderId: '375689014190',
    appId: '1:375689014190:web:e5ad48c2f943496fca363f',
    measurementId: 'G-PQ33KJBNS0',
  },
  development: {
    apiKey: 'AIzaSyAeZs_M5a_FQ4A6irGrWL9VoAVRHRWajiM',
    authDomain: 'topsail-development.firebaseapp.com',
    projectId: 'topsail-development',
    storageBucket: 'topsail-development.firebasestorage.app',
    messagingSenderId: '204456229234',
    appId: '1:204456229234:web:332fa888d32924e0ef85eb',
    measurementId: null,
  },
};

const cfg = firebaseConfigs[env] ?? firebaseConfigs.development;

/* ----------  3️⃣  Initialise Firebase exactly once  --------------- */
export const firebaseApp = getApps().length ? getApps()[0] : initializeApp(cfg);

/**
 * Resolve the optional Firebase App Check site key from browser configuration.
 *
 * Supported sources, in priority order:
 * 1. `window.__TS_APP_CHECK_SITE_KEY__`
 * 2. `<meta name="firebase-app-check-site-key" content="...">`
 * 3. `localStorage.ts_app_check_site_key`
 *
 * The value is expected to be public and environment-specific, so missing keys
 * simply disable App Check instead of throwing during bootstrap.
 *
 * @returns {string} App Check site key when configured, otherwise an empty string.
 */
function resolveAppCheckSiteKey() {
  if (typeof window === 'undefined') {
    return '';
  }

  const globalKey =
    typeof window.__TS_APP_CHECK_SITE_KEY__ === 'string'
      ? window.__TS_APP_CHECK_SITE_KEY__.trim()
      : '';
  if (globalKey) {
    return globalKey;
  }

  const metaKey =
    document
      ?.querySelector?.('meta[name="firebase-app-check-site-key"]')
      ?.getAttribute?.('content')
      ?.trim?.() || '';
  if (metaKey) {
    return metaKey;
  }

  try {
    return localStorage.getItem('ts_app_check_site_key')?.trim?.() || '';
  } catch {
    return '';
  }
}

/**
 * Initialise Firebase App Check when a site key is configured.
 *
 * Missing configuration is treated as an intentional no-op so environments can
 * opt in gradually without breaking browser bootstrap.
 *
 * @returns {import('firebase/app-check').AppCheck|null} App Check instance or null.
 */
function initializeOptionalAppCheck() {
  const siteKey = resolveAppCheckSiteKey();
  if (!siteKey) {
    return null;
  }

  try {
    return initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    console.warn(
      '[firebase-service] App Check not initialised –',
      err?.message || err
    );
    return null;
  }
}

/* ----------  4️⃣  Singletons  ------------------------------------ */
export const auth = getAuth(firebaseApp);
export const appCheck = initializeOptionalAppCheck();
/**
 * Promise that resolves once Firebase Auth persistence is configured.
 * Falls back to session storage when local persistence is unsupported.
 * @type {Promise<void>}
 */
export const authReady = (async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (err) {
    const error =
      /** @type {{ code?: string, message?: string }} */ (err) ?? {};
    const code = typeof error.code === 'string' ? error.code : '';
    const message = typeof error.message === 'string' ? error.message : '';
    const unsupported =
      code === 'auth/unsupported-persistence-type' ||
      /not available|unsupported|does not support/i.test(message);

    if (!unsupported) {
      throw err;
    }

    console.warn('[firebase-service] Falling back to session persistence', err);
    await setPersistence(auth, browserSessionPersistence);
  }
})();
// ---- Firestore: guarded initialization + proxy-friendly runtime switch
// FORCE_LP is true when the user sets localStorage.ts_force_lp = "1"
// or appends ?forceLP=1 to the URL (useful for corporate proxies).
const FORCE_LP =
  (typeof localStorage !== 'undefined' &&
    localStorage.getItem('ts_force_lp') === '1') ||
  (typeof location !== 'undefined' &&
    FORCE_LONG_POLLING_QUERY.test(location.search));

const firestoreSettings = {
  // Disable fetch streams to reduce proxy incompatibilities.
  useFetchStreams: false,
};

if (FORCE_LP) {
  // When forced, skip auto-detect and go straight to long-polling.
  firestoreSettings.experimentalForceLongPolling = true;
} else {
  firestoreSettings.experimentalAutoDetectLongPolling = true;
}

let db;
try {
  // Preferred path: initialize with explicit transport settings once.
  db = initializeFirestore(firebaseApp, firestoreSettings);
} catch {
  // If Firestore was already initialized elsewhere, reuse it.
  if (
    FORCE_LP &&
    typeof console !== 'undefined' &&
    typeof console.warn === 'function'
  ) {
    console.warn(
      '[firebase-service] Forced long-polling requested but existing Firestore instance was reused; transport overrides may not apply.'
    );
  }
  db = getFirestore(firebaseApp);
}
export { db };

export const functions = getFunctions(firebaseApp, 'us-central1'); // ← now after app init

let analytics = null;
if (cfg.measurementId) {
  try {
    analytics = getAnalytics(firebaseApp);
  } catch (err) {
    console.warn('[firebase-service] Analytics not initialised –', err.message);
  }
}
export { analytics };

/**
 * Resolve the current App Check token for custom fetch-based API requests.
 *
 * @param {boolean} [forceRefresh=false] Force a fresh App Check token.
 * @returns {Promise<string|null>} App Check token or null when App Check is not configured.
 */
export async function getAppCheckToken(forceRefresh = false) {
  if (!appCheck) {
    return null;
  }

  try {
    const tokenResult = await getAppCheckSdkToken(appCheck, forceRefresh);
    return tokenResult?.token || null;
  } catch (err) {
    console.warn(
      '[firebase-service] Failed to resolve App Check token –',
      err?.message || err
    );
    return null;
  }
}

/**
 * Resolve the Firestore instance associated with a tenant.
 *
 * The current deployment shares a single Firebase project across tenants,
 * so every tenant maps to the default `db` instance. The helper exists to
 * centralise future overrides where specific tenants may live in separate
 * Firebase apps. Callers should always use this accessor when constructing
 * document references or batches for tenant-scoped data.
 *
 * @param {string} tenantId
 * @returns {import('firebase/firestore').Firestore}
 */
export function getDbForTenant(tenantId) {
  if (!tenantId || typeof tenantId !== 'string') {
    return db;
  }

  return db;
}

/* ----------  5️⃣  Debug helpers (strip in prod if desired)  ------- */
if (!isProduction) {
  Object.assign(window, {
    firebaseApp,
    firebaseConfig: cfg,
    env,
    getAppCheckToken,
    tsForceLongPolling,
  });
}

if (typeof window !== 'undefined') {
  window.__tsGetAppCheckToken = getAppCheckToken;
}
